import { useCallback, useEffect, useState } from "react";
import { api } from "./api.js";
import {
  Avatar, Badge, Button, CardSection, EmptyState, Eyebrow, Field, IconButton, LoadingRows,
  Logo, Notice, PageHeader, Panel, SelectField, cn,
} from "./components/ui.jsx";
import CompaniesPage from "./pages/CompaniesPage.jsx";
import CatalogPage from "./pages/CatalogPage.jsx";
import PlatformCatalogPage from "./pages/PlatformCatalogPage.jsx";
import CompanyDetailPage from "./pages/CompanyDetailPage.jsx";
import PersonDetailPage from "./pages/PersonDetailPage.jsx";
import ListsPage from "./pages/ListsPage.jsx";
import PeoplePage from "./pages/PeoplePage.jsx";
import EmailApprovalsPage from "./pages/EmailApprovalsPage.jsx";
import EmailSettingsPage from "./pages/EmailSettingsPage.jsx";
import AgentContextPage from "./pages/AgentContextPage.jsx";
import ActivityPage from "./pages/ActivityPage.jsx";
import TasksPage from "./pages/TasksPage.jsx";
import LeadsPage from "./pages/LeadsPage.jsx";
import DealsPage from "./pages/DealsPage.jsx";
import SignalsPage from "./pages/SignalsPage.jsx";
import LeadDetailPage from "./pages/LeadDetailPage.jsx";
import LeadPipelineSettingsPage from "./pages/LeadPipelineSettingsPage.jsx";
import HomePage from "./pages/public/HomePage.jsx";
import LoginPage from "./pages/public/LoginPage.jsx";
import InvitePage from "./pages/public/InvitePage.jsx";
import OnboardingPage from "./pages/public/OnboardingPage.jsx";
import SignupPage from "./pages/public/SignupPage.jsx";

const SCOPES = [
  ["crm:read", "Read CRM records and workspace identity"],
  ["crm:write", "Create and update CRM records"],
  ["research:run", "Start and inspect enrichment work"],
  ["outreach:draft", "Prepare outreach for human approval"],
  ["outreach:send", "Request policy-controlled direct delivery"],
  ["schema:manage", "Manage custom fields and list schema"],
  ["admin:audit", "Read workspace audit history"],
];

function ScopePicker({ selected, onChange }) {
  return (
    <fieldset className="grid grid-cols-1 gap-px overflow-hidden rounded-xl border border-neutral-100 bg-neutral-100 sm:grid-cols-2">
      <legend className="mb-3 text-[13px] font-semibold text-muted-foreground">Scopes</legend>
      {SCOPES.map(([scope, description]) => (
        <label className="grid min-h-[62px] cursor-pointer grid-cols-[18px_1fr] items-start gap-2 bg-card p-3 hover:bg-muted" key={scope}>
          <input className="mt-0.5 size-4 accent-primary" type="checkbox" checked={selected.includes(scope)} onChange={(event) => onChange(event.target.checked ? [...selected, scope] : selected.filter((item) => item !== scope))} />
          <span className="grid gap-1"><strong className="text-xs text-foreground">{scope}</strong><small className="text-[11px] font-normal leading-4 text-muted-foreground">{description}</small></span>
        </label>
      ))}
    </fieldset>
  );
}

function CreateKey({ onCreated, onCancel }) {
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState(["crm:read"]);
  const [expiresAt, setExpiresAt] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      const data = await api.createAgentKey({ name, scopes, expires_at: expiresAt || null });
      onCreated(data.agent_key);
    } catch (requestError) {
      setError(requestError.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel>
      <form className="grid grid-cols-1 gap-6 sm:grid-cols-2" onSubmit={submit}>
        <div className="col-span-full flex items-center justify-between"><div className="grid gap-1"><Eyebrow>New credential</Eyebrow><h2 className="text-xl font-bold">Issue an agent key</h2></div><IconButton label="Close key form" onClick={onCancel}>×</IconButton></div>
        {error && <div className="col-span-full"><Notice>{error}</Notice></div>}
        <Field label="Key name" placeholder="e.g. hermes-staging" value={name} onChange={(event) => setName(event.target.value)} maxLength={100} autoFocus required />
        <Field label="Expires on" hint="optional" type="date" value={expiresAt} min={new Date(Date.now() + 86400000).toISOString().slice(0, 10)} onChange={(event) => setExpiresAt(event.target.value)} />
        <div className="col-span-full"><ScopePicker selected={scopes} onChange={setScopes} /></div>
        <div className="col-span-full flex justify-end gap-2"><Button onClick={onCancel}>Cancel</Button><Button variant="primary" type="submit" disabled={busy || scopes.length === 0}>{busy ? "Issuing…" : "Issue key"}</Button></div>
      </form>
    </Panel>
  );
}

function SecretReveal({ value, onDone }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    await navigator.clipboard.writeText(value.token);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }
  return (
    <section className="grid gap-6 rounded-xl border border-primary-100 bg-primary-50 p-6 sm:grid-cols-[52px_1fr]">
      <div className="grid size-[52px] place-items-center rounded-[13px] border border-primary-700 text-xs font-extrabold text-primary">01</div>
      <div className="grid min-w-0 gap-3"><Eyebrow>Shown once</Eyebrow><h2 className="text-xl font-bold">Save the key for {value.name}</h2><p className="text-[13px] leading-6 text-neutral-400">RevPal stores only a cryptographic hash. After closing this panel, the full key cannot be recovered.</p><code className="overflow-x-auto rounded-lg border border-primary-100 bg-card p-4 text-xs text-primary">{value.token}</code><div className="flex flex-wrap gap-2"><Button variant="primary" onClick={copy}>{copied ? "Copied" : "Copy key"}</Button><Button onClick={onDone}>I’ve saved it</Button></div></div>
    </section>
  );
}

function formatDate(value) {
  return value ? new Intl.DateTimeFormat("en", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value)) : "Never";
}

function KeyRows({ keys, onRevoke, revoking }) {
  const [confirming, setConfirming] = useState(null);
  if (!keys.length) return <EmptyState title="No agent keys yet" description="Issue a narrowly scoped key when a server-controlled agent is ready to connect." />;
  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse text-xs">
        <thead><tr className="h-10 bg-muted/50 text-left text-[11px] uppercase tracking-[.06em] text-muted-foreground"><th className="border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Name</th><th className="border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Scopes</th><th className="border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Last used</th><th className="border-b border-border border-r border-r-neutral-100 px-3 font-semibold">Status</th><th className="border-b border-border"><span className="sr-only">Actions</span></th></tr></thead>
        <tbody>{keys.map((key) => (
          <tr className="border-b border-neutral-100 last:border-b-0" key={key.id}>
            <td className="border-r border-neutral-100 px-3 py-2.5"><strong className="block text-[13px]">{key.name}</strong><small className="mt-1 block font-mono text-[11px] text-muted-foreground">{key.display_prefix}</small></td>
            <td className="border-r border-neutral-100 px-3 py-2.5"><div className="flex flex-wrap gap-1">{key.scopes.map((scope) => <Badge key={scope}>{scope}</Badge>)}</div></td>
            <td className="border-r border-neutral-100 px-3 py-2.5 text-muted-foreground">{formatDate(key.last_used_at)}</td>
            <td className="border-r border-neutral-100 px-3 py-2.5"><Badge status={key.status}>{key.status}</Badge></td>
            <td className="whitespace-nowrap px-3 py-2.5 text-right">{key.status === "active" && (confirming === key.id ? <span className="flex gap-1"><Button variant="danger" className="min-h-8 px-2 text-xs" disabled={revoking === key.id} onClick={() => onRevoke(key.id)}>{revoking === key.id ? "Revoking…" : "Confirm"}</Button><Button variant="ghost" className="min-h-8 px-2 text-xs" onClick={() => setConfirming(null)}>Keep</Button></span> : <Button variant="ghost" className="min-h-8 px-2 text-xs" onClick={() => setConfirming(key.id)}>Revoke</Button>)}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function SettingsSectionHeader({ title, description, action }) {
  return (
    <header className="grid gap-4 border-b border-border pb-6 sm:flex sm:items-end sm:justify-between">
      <div className="grid gap-1.5">
        <h2 className="text-xl font-bold tracking-[-0.02em]">{title}</h2>
        <p className="max-w-[68ch] text-[13px] leading-6 text-muted-foreground">{description}</p>
      </div>
      {action}
    </header>
  );
}

function AgentKeysPage({ embedded = false }) {
  const [state, setState] = useState({ loading: true, keys: [], error: "" });
  const [creating, setCreating] = useState(false);
  const [secret, setSecret] = useState(null);
  const [revoking, setRevoking] = useState(null);
  const load = useCallback(async () => {
    try {
      const data = await api.agentKeys();
      setState({ loading: false, keys: data.agent_keys, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function revoke(id) {
    setRevoking(id);
    try { await api.revokeAgentKey(id); await load(); } catch (error) { setState((current) => ({ ...current, error: error.message })); } finally { setRevoking(null); }
  }
  function created(value) { setCreating(false); setSecret(value); load(); }

  return (
    <div className="grid gap-8">
      {embedded
        ? <SettingsSectionHeader title="Agent keys" description="Credentials for server-controlled agents. Grant only the capabilities each agent needs." action={!creating && !secret && <Button variant="primary" onClick={() => setCreating(true)}>Issue key</Button>} />
        : <PageHeader eyebrow="Administration" title="Agent keys" description="Credentials for server-controlled agents. Grant only the capabilities each agent needs." action={!creating && !secret && <Button variant="primary" onClick={() => setCreating(true)}>Issue key</Button>} />}
      {state.error && <Notice>{state.error} <Button variant="ghost" className="ml-2 min-h-8 px-2" onClick={load}>Retry</Button></Notice>}
      {secret && <SecretReveal value={secret} onDone={() => setSecret(null)} />}
      {creating && <CreateKey onCreated={created} onCancel={() => setCreating(false)} />}
      <CardSection title="Workspace credentials" meta={`${state.keys.filter((key) => key.status === "active").length} active`}>{state.loading ? <LoadingRows /> : <KeyRows keys={state.keys} onRevoke={revoke} revoking={revoking} />}</CardSection>
    </div>
  );
}

function MembersPage({ embedded = false }) {
  const [state, setState] = useState({ loading: true, users: [], requests: [], invitations: [], error: "" });
  const [acting, setActing] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteForm, setInviteForm] = useState({ email: "", role: "member" });
  const [inviteResult, setInviteResult] = useState(null);
  const [copied, setCopied] = useState(false);
  const load = useCallback(async () => {
    try {
      const [members, requests, invitations] = await Promise.all([api.members(), api.joinRequests(), api.invitations()]);
      setState({ loading: false, users: members.users, requests: requests.join_requests, invitations: invitations.invitations, error: "" });
    } catch (error) {
      setState((current) => ({ ...current, loading: false, error: error.message }));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function review(id, action) {
    setActing(`${action}:${id}`);
    try { await api.reviewJoinRequest(id, action); await load(); }
    catch (error) { setState((current) => ({ ...current, error: error.message })); }
    finally { setActing(""); }
  }

  async function invite(event) {
    event.preventDefault();
    setActing("invite");
    setInviteResult(null);
    try {
      const data = await api.createInvitation(inviteForm);
      setInviteResult(data);
      setInviteForm({ email: "", role: "member" });
      setInviting(false);
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally { setActing(""); }
  }

  async function invitationAction(id, action) {
    setActing(`${action}:${id}`);
    try {
      const data = action === "resend" ? await api.resendInvitation(id) : await api.revokeInvitation(id);
      if (data.invite_url) setInviteResult(data);
      await load();
    } catch (error) {
      setState((current) => ({ ...current, error: error.message }));
    } finally { setActing(""); }
  }

  async function copyInvite() {
    await navigator.clipboard.writeText(inviteResult.invite_url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  return (
    <div className="grid gap-8">
      {embedded
        ? <SettingsSectionHeader title="Members" description="Invite specific people, review domain-matched access requests, and see everyone with an active role." action={!inviting && <Button variant="primary" onClick={() => setInviting(true)}>Invite member</Button>} />
        : <PageHeader eyebrow="Administration" title="Workspace members" description="Invite specific people, review domain-matched access requests, and see everyone with an active role." action={!inviting && <Button variant="primary" onClick={() => setInviting(true)}>Invite member</Button>} />}
      {state.error && <Notice>{state.error} <Button variant="ghost" className="ml-2 min-h-8 px-2" onClick={load}>Retry</Button></Notice>}
      {inviting && <Panel><form className="grid gap-5 sm:grid-cols-[minmax(0,1fr)_180px_auto] sm:items-end" onSubmit={invite}><div className="sm:col-span-full"><Eyebrow>New invitation</Eyebrow></div><Field label="Email address" type="email" placeholder="colleague@example.com" value={inviteForm.email} autoFocus required onChange={(event) => setInviteForm((current) => ({ ...current, email: event.target.value }))} /><SelectField label="Workspace role" value={inviteForm.role} onChange={(event) => setInviteForm((current) => ({ ...current, role: event.target.value }))}><option value="member">Member</option><option value="admin">Admin</option></SelectField><div className="flex gap-2"><Button onClick={() => setInviting(false)}>Cancel</Button><Button variant="primary" type="submit" disabled={Boolean(acting)}>{acting === "invite" ? "Sending…" : "Send invite"}</Button></div><p className="text-xs leading-5 text-muted-foreground sm:col-span-full">The link expires in seven days and only the invited email can accept it. Admins can manage members and invitations.</p></form></Panel>}
      {inviteResult && <Notice tone="info"><span className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-center"><span><strong className="block text-foreground">Invitation created</strong><span className="block break-all text-xs text-muted-foreground">{inviteResult.invitation.delivery_status === "sent" ? "Email sent. " : "Email delivery was unavailable. "}{inviteResult.invite_url}</span></span><span className="flex gap-2"><Button onClick={copyInvite}>{copied ? "Copied" : "Copy link"}</Button><IconButton label="Dismiss invitation link" onClick={() => setInviteResult(null)}>×</IconButton></span></span></Notice>}
      <CardSection title="Outstanding invitations" meta={`${state.invitations.length} pending`}>
        {state.loading ? <LoadingRows count={2} /> : state.invitations.length ? state.invitations.map((invitation) => <div className="grid min-h-[76px] gap-3 border-b border-neutral-100 py-4 last:border-b-0 sm:grid-cols-[minmax(0,1fr)_auto] sm:items-center" key={invitation.id}><span className="grid min-w-0 gap-1"><strong className="truncate text-xs">{invitation.email}</strong><small className="text-[11px] text-muted-foreground">{invitation.role} · expires {formatDate(invitation.expires_at)}</small><Badge status={invitation.delivery_status === "sent" ? "succeeded" : "failed"}>{invitation.delivery_status === "sent" ? "email sent" : "delivery failed"}</Badge></span><span className="flex flex-wrap gap-2 sm:justify-end"><Button disabled={Boolean(acting)} onClick={() => invitationAction(invitation.id, "resend")}>{acting === `resend:${invitation.id}` ? "Resending…" : "Resend"}</Button><Button variant="danger" disabled={Boolean(acting)} onClick={() => invitationAction(invitation.id, "revoke")}>{acting === `revoke:${invitation.id}` ? "Revoking…" : "Revoke"}</Button></span></div>) : <EmptyState title="No outstanding invitations" description="Invite a colleague by email. Pending invitations and their delivery state will appear here." />}
      </CardSection>
      <CardSection title="Access requests" meta={`${state.requests.length} pending`}>
        {state.loading ? <LoadingRows count={2} /> : state.requests.length ? state.requests.map((request) => <div className="grid min-h-[76px] gap-3 border-b border-neutral-100 py-4 last:border-b-0 sm:grid-cols-[34px_minmax(0,1fr)_auto] sm:items-center" key={request.id}><Avatar name={request.user.name} /><span className="grid min-w-0 gap-0.5"><strong className="truncate text-xs">{request.user.name}</strong><small className="truncate text-[11px] text-muted-foreground">{request.user.email}</small></span><span className="flex flex-wrap gap-2 sm:justify-end"><Button disabled={Boolean(acting)} onClick={() => review(request.id, "reject")}>{acting === `reject:${request.id}` ? "Rejecting…" : "Reject"}</Button><Button variant="primary" disabled={Boolean(acting)} onClick={() => review(request.id, "approve")}>{acting === `approve:${request.id}` ? "Approving…" : "Approve"}</Button></span></div>) : <EmptyState title="No pending access requests" description="Requests from people with a matching work-email domain will appear here for an owner or admin to review." />}
      </CardSection>
      <CardSection title="Active members" meta={`${state.users.length} people`}>
        {state.loading ? <LoadingRows count={2} /> : state.users.map((user) => <div className="grid min-h-[68px] grid-cols-[34px_minmax(0,1fr)_auto] items-center gap-3 border-b border-neutral-100 py-3 last:border-b-0" key={user.membership_id}><Avatar name={user.name} /><span className="grid min-w-0 gap-0.5"><strong className="truncate text-xs">{user.name}</strong><small className="truncate text-[11px] text-muted-foreground">{user.email}</small></span><Badge status="active">{user.role}</Badge></div>)}
      </CardSection>
    </div>
  );
}

const SETTINGS_SECTIONS = [
  ["members", "Members", "People and access"],
  ["leads", "Lead pipeline", "Stages and custom fields"],
  ["email", "Email delivery", "Sender and safeguards"],
  ["keys", "Agent keys", "Scoped credentials"],
];

function appPath(page, settingsSection = "members") {
  if (page === "companies") return "/app";
  if (page === "settings") return `/app/settings/${settingsSection}`;
  return `/app/${page}`;
}

function SidebarNavIcon({ name }) {
  let shape;
  if (name === "companies") shape = <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M8 21V7h8v14M8 11h8M8 15h8" /></>;
  else if (name === "people") shape = <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>;
  else if (name === "tasks") shape = <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="m8 12 2.5 2.5L16 9" /></>;
  else if (name === "leads") shape = <><path d="M4 19V9M10 19V5M16 19v-7M22 19V3" /><path d="M2 19h22" /></>;
  else if (name === "deals") shape = <><circle cx="12" cy="12" r="9" /><path d="M12 7v10M14.5 9.2c-.5-.8-1.4-1.2-2.5-1.2-1.4 0-2.5.8-2.5 2s1 1.7 2.5 2c1.5.3 2.5.8 2.5 2s-1.1 2-2.5 2c-1.1 0-2-.4-2.5-1.2" /></>;
  else if (name === "signals") shape = <><path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" /><path d="M10 21h4" /></>;
  else if (name === "agent-context") shape = <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6M8 13h8M8 17h6" /></>;
  else if (name === "lists") shape = <><rect x="3" y="3" width="18" height="18" rx="2" /><path d="M3 9h18M3 15h18M9 9v12" /></>;
  else if (name === "email-approvals") shape = <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 7L2 7" /></>;
  else if (name === "activity") shape = <path d="M3 12h4l2.5-7 5 14 2.5-7h4" />;
  else if (name === "settings") shape = <><circle cx="12" cy="12" r="3" /><path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.86 2.86-.06-.06A1.7 1.7 0 0 0 15 19.4a1.7 1.7 0 0 0-1 .6 1.7 1.7 0 0 0-.4 1.1V21H9.6v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.88.34l-.06.06-2.86-2.86.06-.06A1.7 1.7 0 0 0 4.1 15a1.7 1.7 0 0 0-1.5-1.1H2.5V10h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.34-1.88l-.06-.06L6.56 4.1l.06.06A1.7 1.7 0 0 0 8.5 4.5 1.7 1.7 0 0 0 9.6 3V2.9h4V3a1.7 1.7 0 0 0 1.1 1.5 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.86 2.86-.06.06A1.7 1.7 0 0 0 19.1 8.9a1.7 1.7 0 0 0 1.5 1.1h.1v3.9h-.1a1.7 1.7 0 0 0-1.2 1.1Z" /></>;
  else shape = <><ellipse cx="12" cy="5" rx="9" ry="3" /><path d="M3 5v6c0 1.66 4.03 3 9 3s9-1.34 9-3V5M3 11v6c0 1.66 4.03 3 9 3s9-1.34 9-3v-6" /></>;

  return (
    <svg className="size-[1.15em]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {shape}
    </svg>
  );
}

function OrganizationSettingsPage({ actor, section, onSectionChange }) {
  function moveTabFocus(event, currentId) {
    const currentIndex = SETTINGS_SECTIONS.findIndex(([id]) => id === currentId);
    let nextIndex = null;
    if (event.key === "ArrowRight") nextIndex = (currentIndex + 1) % SETTINGS_SECTIONS.length;
    if (event.key === "ArrowLeft") nextIndex = (currentIndex - 1 + SETTINGS_SECTIONS.length) % SETTINGS_SECTIONS.length;
    if (event.key === "Home") nextIndex = 0;
    if (event.key === "End") nextIndex = SETTINGS_SECTIONS.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextId = SETTINGS_SECTIONS[nextIndex][0];
    onSectionChange(nextId);
    document.getElementById(`settings-tab-${nextId}`)?.focus();
  }

  return (
    <div className="grid gap-8">
      <PageHeader
        eyebrow={actor.workspace.name}
        title="Organization settings"
        description="Manage the people, delivery controls, and agent credentials shared across this organization."
      />
      <nav className="overflow-x-auto border-b border-border" aria-label="Organization settings">
        <div className="flex min-w-max gap-1" role="tablist" aria-label="Settings sections">
          {SETTINGS_SECTIONS.map(([id, label, description]) => (
            <button
              id={`settings-tab-${id}`}
              className={cn(
                "grid min-h-14 cursor-pointer gap-0.5 rounded-t-lg border-0 border-b-2 border-transparent bg-transparent px-4 pb-3 pt-2 text-left text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                section === id && "border-primary text-foreground",
              )}
              type="button"
              role="tab"
              aria-selected={section === id}
              aria-controls={`settings-panel-${id}`}
              tabIndex={section === id ? 0 : -1}
              key={id}
              onClick={() => onSectionChange(id)}
              onKeyDown={(event) => moveTabFocus(event, id)}
            >
              <strong className="text-[13px]">{label}</strong>
              <small className="text-[10px] font-normal text-muted-foreground">{description}</small>
            </button>
          ))}
        </div>
      </nav>
      <section id={`settings-panel-${section}`} role="tabpanel" aria-labelledby={`settings-tab-${section}`}>
        {section === "email" ? <EmailSettingsPage embedded /> : section === "keys" ? <AgentKeysPage embedded /> : section === "leads" ? <LeadPipelineSettingsPage /> : <MembersPage embedded />}
      </section>
    </div>
  );
}

function AppShell({ actor, onLogout }) {
  const [page, setPage] = useState(() => {
    const pathPage = window.location.pathname.match(/^\/app\/([^/]+)/u)?.[1];
    const requested = pathPage || new URLSearchParams(window.location.search).get("page");
    if (["email-settings", "keys", "members"].includes(requested)) return "settings";
    const allowed = ["companies", "catalog", "people", "leads", "deals", "signals", "tasks", "agent-context", "lists", "email-approvals", "activity", "settings"];
    if (actor.platform_role === "super_admin") allowed.push("platform-catalog");
    return allowed.includes(requested)
      ? requested
      : "companies";
  });
  const [settingsSection, setSettingsSection] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const pathMatch = window.location.pathname.match(/^\/app\/settings\/([^/]+)/u);
    const legacyPage = params.get("page") || window.location.pathname.match(/^\/app\/([^/]+)/u)?.[1];
    if (legacyPage === "email-settings") return "email";
    if (["keys", "members"].includes(legacyPage)) return legacyPage;
    const requested = pathMatch?.[1] || params.get("section");
    return SETTINGS_SECTIONS.some(([id]) => id === requested) ? requested : "members";
  });
  const [detail, setDetail] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => window.localStorage.getItem("revpal:sidebar-collapsed") === "true");
  const [signalCount, setSignalCount] = useState(0);

  useEffect(() => {
    let active = true;
    const refresh = () => api.signalSummary().then((data) => { if (active) setSignalCount(data.summary.attention_count); }).catch(() => {});
    refresh();
    const timer = window.setInterval(refresh, 60_000);
    return () => { active = false; window.clearInterval(timer); };
  }, []);

  useEffect(() => {
    window.localStorage.setItem("revpal:sidebar-collapsed", String(sidebarCollapsed));
  }, [sidebarCollapsed]);

  useEffect(() => {
    const canonical = appPath(page, settingsSection);
    if (`${window.location.pathname}${window.location.search}` !== canonical) {
      window.history.replaceState({}, "", canonical);
    }
  }, [page, settingsSection]);

  function navigate(nextPage) {
    setDetail(null);
    setPage(nextPage);
    window.history.replaceState({}, "", appPath(nextPage, settingsSection));
  }

  function navigateSettings(nextSection) {
    setDetail(null);
    setPage("settings");
    setSettingsSection(nextSection);
    window.history.replaceState({}, "", appPath("settings", nextSection));
  }

  function openDetail(type, id) {
    setDetail((current) => ({ type, id, previous: current, originPage: current?.originPage || page }));
  }

  function detailBackLabel(current) {
    if (current.previous) return current.previous.type === "company" ? "Company" : current.previous.type === "person" ? "Person" : "Lead";
    if (current.originPage === "lists") return "Lists";
    if (current.originPage === "people") return "People";
    if (current.originPage === "leads") return "Leads";
    if (current.originPage === "signals") return "Signals";
    return "Companies";
  }

  let content;
  if (detail?.type === "company") content = <CompanyDetailPage companyId={detail.id} backLabel={detailBackLabel(detail)} canManageCatalog={["owner", "admin"].includes(actor.role)} onBack={() => setDetail(detail.previous)} onOpenPerson={(id) => openDetail("person", id)} onOpenLead={(id) => openDetail("lead", id)} />;
  else if (detail?.type === "person") content = <PersonDetailPage personId={detail.id} backLabel={detailBackLabel(detail)} onBack={() => setDetail(detail.previous)} onOpenCompany={(id) => openDetail("company", id)} onOpenLead={(id) => openDetail("lead", id)} />;
  else if (detail?.type === "lead") content = <LeadDetailPage leadId={detail.id} backLabel={detailBackLabel(detail)} onBack={() => setDetail(detail.previous)} onOpenCompany={(id) => openDetail("company", id)} onOpenPerson={(id) => openDetail("person", id)} />;
  else if (page === "companies") content = <CompaniesPage onOpenCompany={(id) => openDetail("company", id)} />;
  else if (page === "catalog") content = <CatalogPage />;
  else if (page === "people") content = <PeoplePage onOpenPerson={(id) => openDetail("person", id)} />;
  else if (page === "leads") content = <LeadsPage onOpenLead={(id) => openDetail("lead", id)} onOpenCompany={(id) => openDetail("company", id)} />;
  else if (page === "deals") content = <DealsPage onOpenLead={(id) => openDetail("lead", id)} onOpenCompany={(id) => openDetail("company", id)} />;
  else if (page === "signals") content = <SignalsPage onOpenCompany={(id) => openDetail("company", id)} onOpenLead={(id) => openDetail("lead", id)} onCountChange={setSignalCount} />;
  else if (page === "tasks") content = <TasksPage onOpenCompany={(id) => openDetail("company", id)} onOpenPerson={(id) => openDetail("person", id)} onOpenLead={(id) => openDetail("lead", id)} />;
  else if (page === "agent-context") content = <AgentContextPage />;
  else if (page === "lists") content = <ListsPage onOpenCompany={(id) => openDetail("company", id)} onOpenPerson={(id) => openDetail("person", id)} onOpenLead={(id) => openDetail("lead", id)} />;
  else if (page === "email-approvals") content = <EmailApprovalsPage onOpenLead={(id) => openDetail("lead", id)} />;
  else if (page === "activity") content = <ActivityPage />;
  else if (page === "platform-catalog" && actor.platform_role === "super_admin") content = <PlatformCatalogPage />;
  else content = <OrganizationSettingsPage actor={actor} section={settingsSection} onSectionChange={navigateSettings} />;

  return (
    <div className={cn("grid min-h-screen", sidebarCollapsed ? "md:grid-cols-[72px_minmax(0,1fr)]" : "md:grid-cols-[236px_minmax(0,1fr)]")}>
      <aside className={cn("flex border-b border-border bg-sidebar p-3 md:sticky md:top-0 md:h-screen md:flex-col md:border-b-0 md:border-r md:pt-6", sidebarCollapsed ? "md:px-2" : "md:p-4")}>
        <div className={cn("px-2 md:grid md:h-[76px] md:grid-rows-[32px_32px] md:gap-3", sidebarCollapsed ? "md:justify-items-center md:px-0" : "md:justify-items-start")}>
          {sidebarCollapsed ? <><div className="md:hidden"><Logo /></div><div className="hidden md:block"><Logo compact /></div></> : <Logo />}
          <IconButton
            className={cn("hidden size-8 text-foreground md:grid", !sidebarCollapsed && "md:justify-self-end")}
            label={sidebarCollapsed ? "Expand side menu" : "Collapse side menu"}
            title={sidebarCollapsed ? "Expand side menu" : "Collapse side menu"}
            aria-controls="primary-navigation"
            aria-expanded={!sidebarCollapsed}
            onClick={() => setSidebarCollapsed((current) => !current)}
          >
            <svg className="size-[18px]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </IconButton>
        </div>
        <nav id="primary-navigation" className="ml-4 flex gap-1 md:mt-6 md:ml-0 md:grid" aria-label="Primary navigation">
          <p className={cn("mx-3 mb-2 mt-4 hidden text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground md:block", sidebarCollapsed && "md:sr-only")}>Workspace</p>
          {[['companies', 'Companies'], ['people', 'People'], ['leads', 'Leads'], ['deals', 'Deals'], ['signals', 'Signals'], ['tasks', 'Tasks'], ['agent-context', 'Agent context']].map(([id, label]) => <button title={sidebarCollapsed ? label : undefined} className={cn("flex min-h-11 cursor-pointer items-center gap-3 rounded-[9px] border-0 bg-transparent px-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:min-h-10", sidebarCollapsed && "md:justify-center md:px-0", page === id && !detail && "bg-accent font-semibold text-foreground")} key={id} onClick={() => navigate(id)}><span className={cn("hidden w-4 text-center text-muted-foreground sm:inline", sidebarCollapsed && cn("md:w-6 md:text-[18px] md:font-semibold md:leading-none", page === id && !detail ? "md:text-primary" : "md:text-foreground"))}><SidebarNavIcon name={id} /></span><span className={cn(sidebarCollapsed && "md:sr-only")}>{label}</span>{id === "signals" && signalCount > 0 && <span className={cn("ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-bold leading-none text-white", sidebarCollapsed && "md:absolute md:ml-5 md:-mt-5")}>{signalCount > 99 ? "99+" : signalCount}</span>}</button>)}
          <p className={cn("mx-3 mb-2 mt-4 hidden text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground md:block", sidebarCollapsed && "md:sr-only")}>Tables</p>
          <button title={sidebarCollapsed ? "Lists" : undefined} className={cn("flex min-h-11 cursor-pointer items-center gap-3 rounded-[9px] border-0 bg-transparent px-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:min-h-10", sidebarCollapsed && "md:justify-center md:px-0", page === "lists" && "bg-accent font-semibold text-foreground")} onClick={() => navigate("lists")}><span className={cn("hidden w-4 text-center text-muted-foreground sm:inline", sidebarCollapsed && cn("md:w-6 md:text-[18px] md:font-semibold md:leading-none", page === "lists" ? "md:text-primary" : "md:text-foreground"))}><SidebarNavIcon name="lists" /></span><span className={cn(sidebarCollapsed && "md:sr-only")}>Lists</span></button>
          <p className={cn("mx-3 mb-2 mt-4 hidden text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground md:block", sidebarCollapsed && "md:sr-only")}>Outreach</p>
          <button title={sidebarCollapsed ? "Email approvals" : undefined} className={cn("flex min-h-11 cursor-pointer items-center gap-3 rounded-[9px] border-0 bg-transparent px-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:min-h-10", sidebarCollapsed && "md:justify-center md:px-0", page === "email-approvals" && "bg-accent font-semibold text-foreground")} onClick={() => navigate("email-approvals")}><span className={cn("hidden w-4 text-center text-muted-foreground sm:inline", sidebarCollapsed && cn("md:w-6 md:text-[18px] md:font-semibold md:leading-none", page === "email-approvals" ? "md:text-primary" : "md:text-foreground"))}><SidebarNavIcon name="email-approvals" /></span><span className={cn(sidebarCollapsed && "md:sr-only")}>Email approvals</span></button>
          <p className={cn("mx-3 mb-2 mt-4 hidden text-[10px] font-bold uppercase tracking-[.12em] text-muted-foreground md:block", sidebarCollapsed && "md:sr-only")}>Administration</p>
          {[['activity', 'Agent activity'], ['settings', 'Organization settings']].map(([id, label]) => <button title={sidebarCollapsed ? label : undefined} className={cn("hidden min-h-10 cursor-pointer items-center gap-3 rounded-[9px] border-0 bg-transparent px-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:flex", sidebarCollapsed && "md:justify-center md:px-0", page === id && "bg-accent font-semibold text-foreground")} key={id} onClick={() => navigate(id)}><span className={cn("w-4 text-center text-muted-foreground", sidebarCollapsed && cn("md:w-6 md:text-[18px] md:font-semibold md:leading-none", page === id ? "md:text-primary" : "md:text-foreground"))}><SidebarNavIcon name={id} /></span><span className={cn(sidebarCollapsed && "md:sr-only")}>{label}</span></button>)}
          {actor.platform_role === "super_admin" && <button title={sidebarCollapsed ? "Global catalog admin" : undefined} className={cn("hidden min-h-10 cursor-pointer items-center gap-3 rounded-[9px] border-0 bg-transparent px-3 text-left text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground md:flex", sidebarCollapsed && "md:justify-center md:px-0", page === "platform-catalog" && "bg-accent font-semibold text-foreground")} onClick={() => navigate("platform-catalog")}><span className={cn("w-4 text-center text-muted-foreground", sidebarCollapsed && cn("md:w-6 md:text-[18px] md:font-semibold md:leading-none", page === "platform-catalog" ? "md:text-primary" : "md:text-foreground"))}><SidebarNavIcon name="platform-catalog" /></span><span className={cn(sidebarCollapsed && "md:sr-only")}>Global catalog admin</span></button>}
        </nav>
        <div className={cn("ml-auto grid grid-cols-[34px_40px] items-center gap-2 md:mt-auto md:ml-0 md:border-t md:border-neutral-100 md:pt-4", sidebarCollapsed ? "md:grid-cols-1 md:justify-items-center" : "md:grid-cols-[34px_1fr_40px]")}><Avatar name={actor.name} /><span className={cn("hidden min-w-0 gap-0.5 md:grid", sidebarCollapsed && "md:sr-only")}><strong className="truncate text-xs">{actor.name}</strong><small className="truncate text-[11px] text-muted-foreground">{actor.workspace.name}</small></span><IconButton label="Sign out" title="Sign out" onClick={onLogout}>↗</IconButton></div>
      </aside>
      <main className={cn("min-w-0 p-6 sm:p-8", detail ? "lg:px-8 lg:py-10" : "lg:p-[clamp(28px,5vw,68px)]")}><div className={cn("w-full", detail ? "max-w-none" : "max-w-[1280px]")}>{content}</div></main>
    </div>
  );
}

export default function App() {
  const [state, setState] = useState({ status: "booting", actor: null, account: null });
  const [location, setLocation] = useState(() => `${window.location.pathname}${window.location.search}`);

  useEffect(() => {
    const syncLocation = () => setLocation(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", syncLocation);
    return () => window.removeEventListener("popstate", syncLocation);
  }, []);
  useEffect(() => {
    api.me()
      .then((data) => setState({ status: "authenticated", actor: data.actor, account: null }))
      .catch(() => api.onboarding()
        .then((data) => setState({ status: "onboarding", actor: null, account: data.account }))
        .catch(() => setState({ status: "anonymous", actor: null, account: null })));
  }, []);

  function navigate(path, replace = false) {
    window.history[replace ? "replaceState" : "pushState"]({}, "", path);
    setLocation(`${window.location.pathname}${window.location.search}`);
  }

  useEffect(() => {
    const pathname = window.location.pathname;
    const invitationPath = /^\/invite\/[^/]+$/u.test(pathname);
    if (state.status === "authenticated" && ["/login", "/signup", "/onboarding"].includes(pathname)) navigate("/app", true);
    else if (state.status === "authenticated" && pathname === "/" && window.location.search.includes("page=")) navigate(`/app${window.location.search}`, true);
    else if (state.status === "onboarding" && pathname !== "/onboarding" && !invitationPath) navigate("/onboarding", true);
    else if (state.status === "anonymous" && (pathname === "/onboarding" || /^\/app(?:\/|$)/u.test(pathname))) navigate("/login", true);
  }, [state.status, location]);

  function authenticated(actor) {
    setState({ status: "authenticated", actor, account: null });
    navigate("/app", true);
  }

  async function beginOnboarding(account) {
    const invitation = new URLSearchParams(window.location.search).get("invite");
    if (invitation) {
      setState({ status: "onboarding", actor: null, account });
      navigate(`/invite/${encodeURIComponent(invitation)}`, true);
      return;
    }

    setState({ status: "booting", actor: null, account });
    try {
      const onboarding = await api.onboarding();
      if (onboarding.memberships.length === 1) {
        const selected = await api.selectWorkspace(onboarding.memberships[0].workspace.id);
        authenticated(selected.actor);
        return;
      }
    } catch {
      // The organization chooser will surface any persistent onboarding error.
    }
    setState({ status: "onboarding", actor: null, account });
    navigate("/onboarding", true);
  }

  async function logout() {
    try { await api.logout(); } finally {
      setState({ status: "anonymous", actor: null, account: null });
      navigate("/", true);
    }
  }

  if (state.status === "booting") return <main className="grid min-h-screen place-content-center justify-items-center gap-6"><Logo /><span className="h-px w-[72px] animate-pulse bg-primary" /></main>;
  const pathname = window.location.pathname;
  const invitationMatch = pathname.match(/^\/invite\/([^/]+)$/u);
  if (invitationMatch) return <InvitePage token={decodeURIComponent(invitationMatch[1])} authState={state.status} actor={state.actor} account={state.account} onAccepted={() => navigate("/onboarding", true)} onLogout={logout} />;
  if (pathname === "/login" || (/^\/app(?:\/|$)/u.test(pathname) && state.status === "anonymous")) return <LoginPage onAccount={beginOnboarding} />;
  if (pathname === "/signup") return <SignupPage onAccount={beginOnboarding} />;
  if (pathname === "/onboarding" && state.status === "onboarding") return <OnboardingPage onAuthenticated={authenticated} onLogout={logout} />;
  if (/^\/app(?:\/|$)/u.test(pathname) && state.status === "authenticated") return <AppShell actor={state.actor} onLogout={logout} />;
  return <HomePage actor={state.status === "authenticated" ? state.actor : null} />;
}
